import { type Language } from "./schemas";

const LANGUAGE_LABELS = { en: "English", ja: "日本語" } satisfies Record<
  "en" | "ja",
  string
>;

// 動的なlocale値（languageLabels[locale]等）でのアクセス用。noUncheckedIndexedAccess
// により`string | undefined`になるのが正しい（未知のlocaleに対する安全側の型）。
export const languageLabels: Record<string, string> = LANGUAGE_LABELS;

// "ja"/"en"の2値に限定して参照する場合用。値は必ず存在するため`string`型のまま
// 使え、呼び出し側で`?? "日本語"`等のハードコードフォールバックを書く必要がない。
export const fixedLanguageLabels: { en: string; ja: string } = LANGUAGE_LABELS;

// Locales where family name precedes given name
const LAST_NAME_FIRST_LOCALES = ["ja", "zh", "ko", "zh-TW", "zh-HK"];

export function isLastNameFirst(locale: string): boolean {
  return LAST_NAME_FIRST_LOCALES.includes(locale);
}

export function formatPersonName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  locale: string,
): string {
  const parts = isLastNameFirst(locale)
    ? [lastName, firstName]
    : [firstName, lastName];
  return parts.filter(Boolean).join(" ");
}

export function resolveLanguage(
  organization: { language: string },
  project?: { language: string | null },
): Language {
  return (project?.language ?? organization.language) as Language;
}

export function formatName(
  language: Language,
  user: { givenName: string; familyName: string },
) {
  return isLastNameFirst(language)
    ? `${user.familyName} ${user.givenName}`
    : `${user.givenName} ${user.familyName}`;
}

// Room language is authoritative — this directive is appended to every
// prompt (interview/summary/evaluation) so custom prompt text can never
// override it, regardless of what language the prompt body was written in.
const strictLanguageDirectives: Record<Language, string> = {
  ja: "重要: これ以降のすべての応答は必ず日本語のみで行ってください。他の言語への切り替えは禁止します。",
  en: "IMPORTANT: All subsequent responses must be written only in English. Do not switch to any other language.",
};

export function strictLanguageDirective(language: Language): string {
  return strictLanguageDirectives[language];
}

// LiveKitエージェントのシステムプロンプトには実行時刻が一切含まれておらず、
// AIが「今日が何月何日か」を把握できないため「お盆明けのはずなのに実行されて
// いない」等、相対的な期日判断を誤る不具合があった(2026-08 キョウデン様テナント
// フィードバック)。プロンプト文言の調整だけでは解決しない事実情報の欠落なので、
// 毎回のプロンプト生成時にサーバー時刻を明示注入する。基準タイムゾーンは
// テナントの日時設定(organizations.timezone、IANA名)を使う — 日本以外の
// テナントでAsia/Tokyo固定にすると同じ理由でずれた日付を注入することになる。
export function currentDateDirective(
  language: Language,
  timezone: string,
): string {
  const now = new Date();
  const formatted = new Intl.DateTimeFormat(language, {
    timeZone: timezone,
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  }).format(now);

  return language === "ja"
    ? `本日の日付: ${formatted}（${timezone}）`
    : `Today's date: ${formatted} (${timezone})`;
}

// Resolves a bilingual { ja?, en? } text field (or a legacy plain string)
// down to a single string for the given language, falling back to the other
// language if the requested one is missing.
export function resolveBilingualText(
  text: string | { ja?: string; en?: string } | null | undefined,
  language: Language,
): string {
  if (!text) return "";
  if (typeof text === "string") return text.trim();
  const other: Language = language === "ja" ? "en" : "ja";
  return (text[language] || text[other] || "").trim();
}

// UIロケール（"ja"/"en"）をIntl API（toLocaleString等）が期待する
// BCP 47ロケールタグへ変換する。
const INTL_LOCALE_TAGS: Record<string, string> = {
  ja: "ja-JP",
  en: "en-US",
};

export function toIntlLocale(locale: string): string {
  return INTL_LOCALE_TAGS[locale] ?? "en-US";
}
